import { escapeHtml } from "@brains/utils/string-utils";
import { essentialHeadTags, type EssentialHeadPaths } from "./essential-head";

/**
 * Generates the HTML shell for the page
 * Head content is provided as a string from the head collector
 */
export function createHTMLShell(
  content: string,
  headContent?: string,
  defaultTitle?: string,
  themeMode?: "light" | "dark",
  analyticsScript?: string,
  headScripts?: string[],
  headPaths?: EssentialHeadPaths,
  language = "en",
): string {
  // Default head content if none provided
  const defaultHead = `
    ${essentialHeadTags(headPaths).join("\n    ")}
    <title>${escapeHtml(defaultTitle ?? "Site")}</title>`;

  const themeAttr = ` data-theme="${themeMode ?? "dark"}"`;

  // Theme and UI scripts - runs immediately to prevent flash
  const uiScript = `
    <script>
      (function() {
        // Theme handling
        const stored = localStorage.getItem('theme');
        const theme = stored || '${themeMode ?? "dark"}';
        document.documentElement.setAttribute('data-theme', theme);

        window.toggleTheme = function() {
          const current = document.documentElement.getAttribute('data-theme');
          const next = current === 'dark' ? 'light' : 'dark';
          document.documentElement.setAttribute('data-theme', next);
          localStorage.setItem('theme', next);
        };

        // Mobile menu handling
        window.toggleMobileMenu = function() {
          const menu = document.getElementById('mobile-menu');
          const button = document.getElementById('mobile-menu-button');
          if (!menu || !button) return;

          const isOpen = !menu.classList.contains('max-h-0');
          const willOpen = !isOpen;

          // Toggle height and opacity classes
          menu.classList.toggle('max-h-0');
          menu.classList.toggle('max-h-screen');
          menu.classList.toggle('opacity-0');
          menu.classList.toggle('opacity-100');

          button.setAttribute('aria-expanded', String(willOpen));

          // Toggle icon visibility
          const menuIcon = button.querySelector('.menu-icon');
          const closeIcon = button.querySelector('.close-icon');
          if (menuIcon && closeIcon) {
            menuIcon.classList.toggle('hidden');
            closeIcon.classList.toggle('hidden');
          }

          if (willOpen) {
            // Focus first link when menu opens
            const firstLink = menu.querySelector('a');
            if (firstLink) {
              setTimeout(function() { firstLink.focus(); }, 100);
            }
            // Add keyboard listeners
            document.addEventListener('keydown', window.handleMobileMenuKeydown);
          } else {
            // Return focus to button when menu closes
            button.focus();
            // Remove keyboard listeners
            document.removeEventListener('keydown', window.handleMobileMenuKeydown);
          }
        };

        // Close mobile menu (for link clicks)
        window.closeMobileMenu = function() {
          const menu = document.getElementById('mobile-menu');
          if (menu && !menu.classList.contains('max-h-0')) {
            window.toggleMobileMenu();
          }
        };

        // Handle keyboard events for mobile menu
        window.handleMobileMenuKeydown = function(e) {
          const menu = document.getElementById('mobile-menu');
          const button = document.getElementById('mobile-menu-button');
          if (!menu || !button) return;

          // Close on Escape
          if (e.key === 'Escape') {
            window.closeMobileMenu();
            return;
          }

          // Focus trap on Tab
          if (e.key === 'Tab') {
            const focusableElements = menu.querySelectorAll('a, button');
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (e.shiftKey) {
              // Shift+Tab: wrap from first to last (or button)
              if (document.activeElement === firstElement || document.activeElement === button) {
                e.preventDefault();
                lastElement.focus();
              }
            } else {
              // Tab: wrap from last to button
              if (document.activeElement === lastElement) {
                e.preventDefault();
                button.focus();
              }
            }
          }
        };

        // React static markup intentionally omits string event-handler props.
        // Bind the server-rendered controls once at the document boundary.
        document.addEventListener('click', function(e) {
          const target = e.target;
          if (!(target instanceof Element)) return;

          if (target.closest('[data-theme-toggle]')) {
            window.toggleTheme();
          }
          if (target.closest('#mobile-menu-button')) {
            window.toggleMobileMenu();
          }
          if (target.closest('#mobile-menu a')) {
            window.closeMobileMenu();
          }
        });
      })();
    </script>`;

  // Analytics script (legacy — prefer headScripts from plugin registration)
  const analyticsTag = analyticsScript ? `\n    ${analyticsScript}` : "";

  // Head scripts registered by plugins (e.g., analytics, newsletter)
  const headScriptTags =
    headScripts && headScripts.length > 0
      ? "\n    " + headScripts.join("\n    ")
      : "";

  return `<!DOCTYPE html>
<html lang="${escapeHtml(language)}" class="h-full"${themeAttr}>
<head>
    ${headContent ?? defaultHead}
    ${uiScript}${analyticsTag}${headScriptTags}
</head>
<body class="h-full font-sans">
  <div id="root" class="min-h-screen">
    ${content}
  </div>
</body>
</html>`;
}
